import * as PopoverPrimitive from '@radix-ui/react-popover';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import { cn } from '../lib/cn';

export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverAnchor = PopoverPrimitive.Anchor;
// Closes through Radix's own dismiss path, so an explicit ✕ returns focus to the trigger
// exactly the way Escape and click-away already do. A `setOpen(false)` button does not.
export const PopoverClose = PopoverPrimitive.Close;
// Re-export Portal so callers can use it directly when they need a stable container.
export const PopoverPortal = PopoverPrimitive.Portal;

export const PopoverContent = forwardRef<
  ElementRef<typeof PopoverPrimitive.Content>,
  ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & {
    /**
     * DOM node to portal into. Defaults to `document.body`.
     * Pass a stable ref (e.g. a wrapper div at the app root) to avoid
     * React 18 StrictMode / Radix Portal `removeChild` race conditions.
     */
    container?: HTMLElement | null;
  }
>(({ className, align = 'center', sideOffset = 6, container, ...props }, ref) => (
  <PopoverPrimitive.Portal container={container}>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        'z-50 w-72 rounded-lg border border-border-strong bg-bg-elevated p-4 text-text-primary shadow-[var(--menu-shadow)]',
        'focus-visible:outline-none',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
        'data-[state=open]:zoom-in-[0.98] data-[state=closed]:zoom-out-[0.98]',
        className,
      )}
      {...props}
    />
  </PopoverPrimitive.Portal>
));
PopoverContent.displayName = 'PopoverContent';
